import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { customerIdSchema, facilityIdSchema } from "../lib/workIds";

const workFacilitySchema = z.object({
  id: facilityIdSchema,
  name: z.string(),
  role: z.enum(["REQUESTER", "OPERATOR", "TECHNICIAN", "ADMINISTRATOR"]),
  // The Facility's IANA timezone, authoritative for Work Order due-date
  // display and filtering.
  timezone: z.string().min(1),
  // The Facility's SAP Plant Code (exactly 4 letters or digits), or null.
  // Defaults to null so a response from a backend that predates the field
  // still parses during a rolling deploy.
  sapPlantCode: z.string().nullable().default(null),
});

const workCustomerSchema = z.object({
  id: customerIdSchema,
  name: z.string(),
  facilities: z.array(workFacilitySchema),
});

const workContextResponseSchema = z.object({
  customers: z.array(workCustomerSchema),
});

export type WorkFacility = z.infer<typeof workFacilitySchema>;
export type WorkCustomer = z.infer<typeof workCustomerSchema>;
export type WorkContextResponse = z.infer<typeof workContextResponseSchema>;

export class WorkContextRequestError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Failed to fetch Work context: ${status}`);
    this.name = "WorkContextRequestError";
    this.status = status;
  }
}

export function isWorkAccessDeniedError(error: unknown) {
  return error instanceof WorkContextRequestError && error.status === 403;
}

async function fetchWorkContext(): Promise<WorkContextResponse> {
  const response = await workClient.v1["current-user"].context.$get();
  if (!response.ok) {
    throw new WorkContextRequestError(response.status);
  }

  return workContextResponseSchema.parse(await response.json());
}

export function useWorkContextQuery() {
  return useQuery({
    queryKey: ["work-context"],
    queryFn: fetchWorkContext,
    // A denied user is not retried, and the access gate owns the denied and
    // error states inline, so no toast is raised on top of them.
    retry: (failureCount, error) =>
      !isWorkAccessDeniedError(error) && failureCount < 3,
  });
}
